<template>
  <div>
    <div class="flex flex-row items-center">
      <p class="font-semibold text-3xl">
        {{ $route.meta.title }}
      </p>
      <div class="bg-primary text-white font-semibold ml-2 px-2 py-1 rounded-xl">
        {{ $route.params.tug ? $route.params.tug.name : tug.name }}
      </div>
    </div>
    <div
      v-for="(itemForm, indexForm) of form"
      class="bg-white rounded-2xl mt-8 p-8"
      :key="`form-${indexForm}`"
      :id="`form-${indexForm}`"
    >
      <span class="text-xl font-semibold text-grey">
        {{ indexForm+1 }}. {{ itemForm.title }}
      </span>
      <data-table
        :table-header="tugCertificateHeaders"
        :table-data="itemForm.data"
        body-height="100vh"
      >
        <template #paper_type="{ row, index }">
          <div class="flex flex-row font-semibold w-text-certificate">
            <span>{{ index+1 }}.</span>
            <div class="flex flex-col ml-1 font-semibold whitespace-normal">
              {{ row.paper_type.title }}
              <span class="text-sm font-normal mt-1 whitespace-normal">{{ row.paper_type.subtitle }}</span>
            </div>
          </div>
        </template>
        <template #published_by="{ row }">
          <vs-input
            class="w-form-certificate"
            placeholder="Diterbitkan oleh"
            v-model="row.published_by.value"
            :class="{ 'is-not-valid': !row.published_by.isValid }"
            :disabled="isDetail()"
          />
        </template>
        <template #date="{ row }">
          <date-picker
            class="w-form-certificate"
            type="date"
            value-type="YYYY-MM-DD"
            format="DD-MMM-YYYY"
            placeholder="dd-mm-yyyy"
            v-model="row.date.value"
            :class="{ 'is-not-valid': !row.date.isValid }"
            :disabled="isDetail()"
          />
        </template>
        <template #endorsement="{ row }">
          <vs-input
            class="w-form-certificate"
            placeholder="Endorsement"
            v-model="row.endorsement.value"
            :class="{ 'is-not-valid': !row.endorsement.isValid }"
            :disabled="isDetail()"
          />
        </template>
        <template #valid="{ row }">
          <date-picker
            class="w-form-certificate"
            type="date"
            value-type="YYYY-MM-DD"
            format="DD-MMM-YYYY"
            placeholder="dd-mm-yyyy"
            v-model="row.valid.value"
            :class="{ 'is-not-valid': !row.is_expired.value && !row.valid.isValid }"
            :disabled-date="(date) => disabledDate(date, row.date.value)"
            :disabled="row.is_expired.value || isDetail()"
          />
        </template>
        <template #is_expired="{ row, index }">
          <div class="flex flex-row items-center">
            <vs-checkbox
              color="secondary"
              v-model="row.is_expired.value"
              :disabled="isDetail()"
              @input="(val) => resetValidityDate(val, indexForm, index)"
            />
            Tetap
          </div>
        </template>
      </data-table>
    </div>

    <div class="mt-16 flex items-center justify-end">
      <vs-button
        class="button"
        type="border"
        color="secondary"
        @click="$router.push({ name: $route.meta.parent.name })"
      >
        Back
      </vs-button>
      <vs-button
        v-if="!isDetail()"
        class="button shadow-secondary ml-6"
        color="secondary"
        @click="postTugCertificates"
      >
        Save Certificate
      </vs-button>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'

import DataTable from '@/components/dataTable'
import DatePicker from '@/components/datePicker'
import { PAGE_TITLE, VALID_CATEGORY_CERTIFICATE } from '@/fixtures/Constants'
import TugCertificate from '@/fixtures/tug-certificate-form.json'

export default {
  metaInfo() {
    return {
      title: `${PAGE_TITLE} ${this.$route.meta.title}`
    }
  },
  components: {
    DataTable,
    DatePicker
  },
  data() {
    return {
      form: JSON.parse(JSON.stringify(TugCertificate.form)),
      tugCertificateHeaders: [
        { key: 'paper_type', label: 'Jenis Surat/Sertifikat/Dokumen Kapal' },
        { key: 'published_by', label: 'Diterbitkan Oleh' },
        { key: 'date', label: 'Tanggal' },
        { key: 'endorsement', label: 'Endorsement' },
        { key: 'valid', label: 'Berlaku S/d' },
        { key: 'is_expired', label: '' }
      ]
    }
  },
  mounted() {
    this.initialize()
  },
  computed: {
    ...mapGetters({
      tug: 'Contract/tugIdName',
      tugCertificate: 'Contract/tugCertificate'
    })
  },
  watch: {
    tugCertificate: {
      immediate: true,
      handler(val) {
        if (val && !this.isEmpty(val)) {
          this.transformDataToForm(val)
        }
      }
    }
  },
  methods: {
    async initialize() {
      await this.$store.dispatch('Contract/setTugIdName', this.$route.params.tug || this.tug)
      await this.fetchTugCertificates()
    },
    async fetchTugCertificates() {
      try {
        await this.$store.dispatch('Contract/fetchTugCertificates', this.$route.params.tug?.id || this.tug.id)
      } catch (e) {
        this.showFailedNotification(this, 'Failed to fetch barge certificate, please try again')
      }
    },
    async postTugCertificates() {
      try {
        if (this.validate()) {
          this.showFailedNotification(this, 'Some fields is empty, please fill it first')
          return
        }

        await this.$store.dispatch('Contract/postTugCertificates', {
          id: this.$route.params.tug?.id || this.tug.id,
          form: this.transformFormToData()
        })
        this.showSuccessNotification(this, 'You have successfully save certificate')
        await this.$router.push({ name: this.$route.meta.parent.name })
      } catch (e) {
        this.showFailedNotification(this, 'Failed to save tug certificate, please try again')
      }
    },
    isDetail() {
      return this.$route.name.includes('Detail')
    },
    transformFormToData() {
      const form = []
      this.form.forEach(category =>
        category.data.forEach(({ paper_type, ...paper }) =>
          form.push({
            category: VALID_CATEGORY_CERTIFICATE[category.title],
            ...this.getValueFromForm(paper),
            name: this.toUpper(paper_type.title)
          }))
      )
      return {
        type: 'TUG',
        data: form
      }
    },
    getValueFromForm(data) {
      const item = {}
      Object.entries(data).forEach(([key, itemForm]) => {
        item[key] = itemForm.value
      })
      return item
    },
    transformDataToForm(val) {
      this.form = this.form.map(categoryItem => ({
        ...categoryItem,
        data: categoryItem.data.map(({ paper_type }) => {
          const { category, name, id, updated_at, ...dataPaper } = val.find(item =>
            item.category === VALID_CATEGORY_CERTIFICATE[categoryItem.title] && item.name === this.toUpper(paper_type.title)
          )
          return {
            paper_type,
            ...this.assignDataToValue(dataPaper)
          }
        })
      }))
    },
    assignDataToValue(data) {
      const item = {}
      Object.entries(data).forEach(([key, value]) => {
        item[key] = {
          isValid: true,
          value: value
        }
      })
      return item
    },
    disabledDate(date, publishDate) {
      if (publishDate) {
        return date < this.moment(publishDate, ['YYYY-MM-DD'])
      }

      return date
    },
    validate() {
      this.form.forEach(category => {
        category.data.forEach(({ paper_type, ...paper }) => {
          Object.keys(paper).forEach(key => {
            if (!(key === 'valid' && paper['is_expired'].value) && key !== 'is_expired') {
              paper[key].isValid = !!paper[key].value
            }
          })
        })
      })

      return this.form.some(category =>
        category.data.some(({ paper_type, ...paper }) =>
          Object.keys(paper).some(key => !paper[key].isValid)
        )
      )
    },
    resetValidityDate(val, indexForm, index) {
      if (val) {
        this.form[indexForm].data[index].valid.value = null
        this.form[indexForm].data[index].valid.isValid = true
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.w-form-certificate {
  width: 140px !important;
}

.w-text-certificate {
  width: 220px;
}

/deep/.vs-table--td {
  background-color: #F1F1FD !important;
}

/deep/.vs-table--td,
/deep/.vs-table--th {
  @apply pl-0 #{!important};
  @apply pr-3 #{!important};

  &:first-child {
    @apply pl-5 #{!important};
  }

  &:last-child {
    @apply pr-5 #{!important};
  }
}

/deep/.vs-input--input,
/deep/.mx-input-wrapper {
  @apply bg-white #{!important};

  &:disabled {
    background-color: #ECEBED !important;
  }
}

/deep/.vs-checkbox {
  @apply bg-white;
}

.button {
  @apply py-4 #{!important};
  @apply rounded-xl;
  @apply border-2;
  @apply font-semibold;
  @apply text-base;
  width: 140px;
  height: calc( 1rem * 10 / 3 );

  &.shadow-secondary {
    -webkit-box-shadow: 0 8px 25px -8px rgba(var(--vs-secondary),1);
    box-shadow: 0 8px 25px -8px rgba(var(--vs-secondary),1);
  }

  &:focus {
    @apply outline-none;
  }
}

.is-not-valid {
  /deep/.vs-con-input,
  /deep/.mx-input-wrapper {
    @apply rounded-lg;
    outline: 2px solid #ff647c;
  }
}
</style>
